import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound, CheckCircle2 } from "lucide-react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { COMPANY } from "@/data/company";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const input = "w-full mt-1 rounded-lg bg-white/10 border border-white/20 px-4 py-3 text-white text-sm outline-none focus:ring-2 focus:ring-primary";

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw.length < 8) { setError("Le mot de passe doit contenir au moins 8 caractères"); return; }
    if (pw !== confirm) { setError("La confirmation ne correspond pas"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      setTimeout(() => nav("/admin/login"), 2500);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-ink flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <img src={fileUrl(COMPANY.logo)} alt="SMT Provence" className="h-16 mx-auto mb-4 bg-white/5 rounded-lg p-2" />
          <h1 className="font-heading text-2xl font-black text-white">Nouveau mot de passe</h1>
          <p className="text-white/50 text-sm mt-1">Choisissez un nouveau mot de passe</p>
        </div>

        {!token ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center">
            <p className="text-red-400 text-sm" data-testid="reset-no-token">Lien invalide : aucun jeton fourni. Refaites une demande depuis la page de connexion.</p>
            <button onClick={() => nav("/admin/login")} className="mt-4 text-primary text-sm font-semibold hover:underline">Retour à la connexion</button>
          </div>
        ) : done ? (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8 text-center" data-testid="reset-success">
            <CheckCircle2 className="mx-auto text-green-400 mb-3" size={40} />
            <p className="text-white font-semibold">Mot de passe modifié !</p>
            <p className="text-white/50 text-sm mt-1">Redirection vers la connexion…</p>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="reset-form" className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-2xl p-8 space-y-4">
            <div>
              <label className="text-xs font-semibold text-white/60">Nouveau mot de passe</label>
              <input data-testid="reset-new-pw" type="password" value={pw} onChange={(e) => setPw(e.target.value)} className={input} placeholder="Min. 8 caractères" />
            </div>
            <div>
              <label className="text-xs font-semibold text-white/60">Confirmer</label>
              <input data-testid="reset-confirm-pw" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} className={input} placeholder="••••••••" />
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-red-400">{error}</p>}
            <button type="submit" disabled={loading} data-testid="reset-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-primary text-white font-bold py-3 rounded-full hover:brightness-95 disabled:opacity-60">
              {loading ? <Loader2 className="animate-spin" size={18} /> : <KeyRound size={16} />} Réinitialiser
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
